import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { BsXLg } from 'react-icons/bs';
import { createPortal } from 'react-dom';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import planConfigSlice from '../../../core/planConfig/slice';
import CancelPenaltyRow from '../../booking/meal-plan/PlanConfig/CancelPenaltyRow';
import type { MealPlanConfigModal } from '../../booking/meal-plan/PlanConfig/PlanConfig';
import ImageWithFallback from '../ImageWithFallback';
import useDisabledBodyScrolling from '../../../core/hooks/useDisabledBodyScrolling';
import Down from '../Icons/Down';
import tailwindConfig from '../../../../tailwind.config';

const descriptionGrouped = (description: string) => description.split('\r\n');

type Props = {
  mealPlanConfigModal: MealPlanConfigModal;
  actionButton: React.ReactNode;
};

const PlanDetailModal = ({ mealPlanConfigModal, actionButton }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isCancelPolicyOpen, setIsCancelPolicyOpen] = useState(false);
  const { newlySelectedMealPlan } = mealPlanConfigModal;

  const onCloseModal = () => {
    dispatch(planConfigSlice.actions.openMealPlanConfigModal(undefined));
  };

  useEffect(() => {
    const closeModal = (e: MouseEvent) => {
      return (e.target as HTMLElement).id === 'backdrop' && onCloseModal();
    };

    document.addEventListener('click', closeModal);
    return () => document.removeEventListener('click', closeModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDisabledBodyScrolling();

  const cancelPolicy = (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setIsCancelPolicyOpen(!isCancelPolicyOpen)}
        className="flex items-center gap-1 text-orange-700"
      >
        <p className="text-small">{t('hotel:policy.cancel_policy')}</p>
        <p
          className={`${
            isCancelPolicyOpen ? 'ease rotate-180 duration-100' : 'ease duration-100'
          } inline-block`}
        >
          <Down size={16} color={tailwindConfig.theme.colors.orange[700]} />
        </p>
      </button>
      <div
        className={`overflow-hidden bg-red-100 text-small ${
          isCancelPolicyOpen ? 'block h-auto p-3' : 'hidden'
        }`}
      >
        <div>
          <p>
            {t('common:payment.in_the_case_of', {
              mealPlanName: newlySelectedMealPlan.name,
            })}
          </p>
          <hr className="my-2.5 border-red-100" />
        </div>

        <table className="w-full">
          <tbody>
            {newlySelectedMealPlan.planPenalties?.cancelPenalties?.map((cancelPenalty) => {
              const key = `${cancelPenalty.type || ''}${cancelPenalty.deadline}`;
              return (
                <CancelPenaltyRow
                  key={key}
                  cancelPenalty={cancelPenalty}
                  keyTextClassName="text-small sm:w-1/5"
                  customValueTextClassName="text-small"
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const description = (
    <div className="flex flex-col gap-3">
      {newlySelectedMealPlan.description &&
        descriptionGrouped(newlySelectedMealPlan.description).map((x) => (
          <p className="text-small" key={x}>
            {x}
          </p>
        ))}
    </div>
  );

  return createPortal(
    <div id="backdrop" className="fixed inset-0 z-100 flex bg-black/40">
      {/* mobile */}
      <div id="modal" className="mt-6 block h-auto w-full rounded-t-2xl bg-white sm:hidden">
        <div className="flex h-full flex-col">
          <div className="flex w-full justify-between p-4">
            <div className="grow break-words text-medium font-bold">
              <p>{newlySelectedMealPlan.name || t('component:plan_config.title')}</p>
            </div>
            <div className="flex-none">
              <button type="button" onClick={onCloseModal}>
                <BsXLg className="text-gray-600" size={24} />
              </button>
            </div>
          </div>
          <div className="flex h-full flex-col gap-2 overflow-auto px-4">
            <div className="relative w-full">
              <ImageWithFallback
                src={newlySelectedMealPlan.imageUrl || '/no-plan-selected-desktop.svg'}
                alt="meal-plan-pic-desktop"
                className="mb-3 rounded-md"
                fallbackSrc="/no-plan-selected-desktop.svg"
                layout="responsive"
                width={700}
                height={500}
                objectFit="cover"
              />
            </div>
            {cancelPolicy}
            {description}
          </div>

          <div className="flex justify-end p-4 shadow-[0px_-20px_20px_0px_rgba(0,_0,_0,_0.056)]">
            {actionButton}
          </div>
        </div>
      </div>

      {/* desktop */}
      <div
        id="modal"
        className="m-auto hidden h-3/4 w-full rounded-3xl bg-white sm:block sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl"
      >
        <div className="relative flex h-full flex-col justify-between rounded-2xl bg-white">
          <button className="absolute right-6 top-6" type="button" onClick={onCloseModal}>
            <BsXLg className="text-gray-600" size={24} />
          </button>
          <div className="fixed m-6 h-40 w-80 sm:h-48">
            <ImageWithFallback
              src={newlySelectedMealPlan.imageUrl || '/no-plan-selected-desktop.svg'}
              alt="meal-plan-pic-desktop"
              className="mb-3 rounded-md"
              fallbackSrc="/no-plan-selected-desktop.svg"
              layout="fill"
              objectFit="cover"
            />
          </div>
          {/* ml === image width: 320px(w-80) + 24px(m-6)  */}
          <div className="ml-[344px] flex h-full flex-col pt-6">
            <div className="break-words bg-white pl-6 text-left text-medium font-bold sm:w-[230px] md:w-[370px] lg:w-[630px] xl:w-[880px]">
              {newlySelectedMealPlan.name || t('component:plan_config.title')}
            </div>
            <div className="flex size-full flex-col gap-2 overflow-auto px-6 pt-2">
              {cancelPolicy}
              {description}
            </div>
            {/* ml === - image width, to offset the ml of text content */}
            <div className="z-10 ml-[-344px] flex flex-row justify-end gap-10 rounded-b-2xl bg-white p-4 px-8 py-5 shadow-[0px_-20px_20px_0px_rgba(0,_0,_0,_0.056)]">
              {actionButton}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('app') as Element
  );
};

export default PlanDetailModal;
